import $ from "jquery";

class BiometricService {
  constructor() {
    this.RD_SERVICE_BASE_URL = "http://127.0.0.1";
    this.DEFAULT_PORTS = [
      11100, 11101, 11102, 11103, 11104, 11105, 11106, 11107, 11108, 11109,
      11110, 11111, 11112, 11113, 11114, 11115, 11116, 11117, 11118, 11119,
      11120,
    ];
    this.TIMEOUT = 30000;
    this.MAX_RETRIES = 3;

    this.DEFAULT_PID_OPTIONS = {
      ver: "1.0",
      fCount: "1",
      fType: "2", // 0 = FMR, 1 = FIR, 2 = Both
      iCount: "0",
      iType: "", // Added for IDEMIA compatibility
      pCount: "0",
      pType: "", // Added for IDEMIA compatibility
      format: "0",
      pidVer: "2.0",
      timeout: "15000",
      posh: "", // Must be empty string for IDEMIA
      env: "P",
      wadh: "",
      otp: "",
    };
  }

  async checkServiceStatus() {
    for (const port of this.DEFAULT_PORTS) {
      try {
        const result = await this.discoverDevice(port);
        if (result.success) {
          return {
            serviceAvailable: true,
            deviceInfo: result.device,
            port: result.port,
            message: "Device found and ready",
          };
        }
      } catch (error) {
        continue;
      }
    }
    return {
      serviceAvailable: false,
      error:
        "No biometric device found. Please ensure RD Service is running and device is connected.",
      errorCode: "DEVICE_NOT_FOUND",
    };
  }

  discoverDevice(port) {
    return new Promise((resolve, reject) => {
      const url = `${this.RD_SERVICE_BASE_URL}:${port}`;
      $.support.cors = true;

      $.ajax({
        type: "RDSERVICE",
        async: true,
        crossDomain: true,
        url: url,
        contentType: "text/xml; charset=utf-8",
        dataType: "text",
        processData: false,
        cache: false,
        timeout: 5000,
        success: (data) => {
          if (!data || !data.includes("<RDService")) {
            reject(new Error("Invalid response from RD Service"));
            return;
          }
          const deviceInfo = this.parseDeviceResponse(data, port);
          deviceInfo.isMantra =
            deviceInfo.info && deviceInfo.info.toLowerCase().includes("mantra");
          if (deviceInfo.status === "READY") {
            resolve({ success: true, device: deviceInfo, port: port });
          } else {
            reject(new Error(`Device not ready: ${deviceInfo.status}`));
          }
        },
        error: (jqXHR, textStatus) =>
          reject(new Error(`Port ${port}: ${textStatus}`)),
      });
    });
  }

  parseDeviceResponse(xmlString, port) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    const rdService = xmlDoc.querySelector("RDService");
    const captureInterface = xmlDoc.querySelector('Interface[id="CAPTURE"]');
    const deviceInfoInterface = xmlDoc.querySelector(
      'Interface[id="DEVICEINFO"]',
    );

    let capturePath = captureInterface?.getAttribute("path") || "/capture";
    let deviceInfoPath = deviceInfoInterface?.getAttribute("path") || "/info";

    if (capturePath && !capturePath.startsWith("/"))
      capturePath = "/" + capturePath;
    if (deviceInfoPath && !deviceInfoPath.startsWith("/"))
      deviceInfoPath = "/" + deviceInfoPath;

    return {
      status: rdService?.getAttribute("status") || "UNKNOWN",
      info: rdService?.getAttribute("info") || "",
      capturePath: capturePath,
      deviceInfoPath: deviceInfoPath,
      port: port,
      timestamp: new Date().toISOString(),
    };
  }

  captureFingerprint(deviceInfo, options = {}) {
    return new Promise((resolve, reject) => {
      //

      // Inside captureFingerprint, after getting deviceInfo
      let capturePath = deviceInfo.capturePath;
      // if (!deviceInfo.isMantra) {
      //   // For Morpho/IDEMIA, force using /rd/capture if available, else fallback
      //   // Option 1: Always try /rd/capture first (if the device supports it)
      //   capturePath = "/rd/capture";
      // }
      // const url = `${this.RD_SERVICE_BASE_URL}:${deviceInfo.port}${capturePath}`;

      const url = `${this.RD_SERVICE_BASE_URL}:${deviceInfo.port}${deviceInfo.capturePath}`;
      const pidOptions = { ...this.DEFAULT_PID_OPTIONS, ...options };
      const xmlRequest = this.buildPidOptionsXml(deviceInfo, pidOptions);

      console.log("Sending capture request to:", url);
      console.log("XML Request:", xmlRequest);

      $.support.cors = true;
      $.ajax({
        type: "CAPTURE",
        async: true,
        crossDomain: true,
        url: url,
        data: xmlRequest,
        contentType: "text/xml; charset=utf-8",
        dataType: "text",
        processData: false,
        cache: false,
        timeout: this.TIMEOUT,
        success: (data) => {
          if (!data || !data.includes("<Resp")) {
            reject(new Error("Invalid capture response"));
            return;
          }
          const captureData = this.parseCaptureResponse(data);
          if (captureData.errorCode !== "0") {
            reject(new Error(`Capture failed: ${captureData.errorInfo}`));
            return;
          }
          resolve({
            success: true,
            data: {
              ...captureData,
              deviceInfo: {
                dpId: captureData.dpId,
                rdsId: captureData.rdsId,
                rdsVer: captureData.rdsVer,
                dc: captureData.dc,
                mi: captureData.mi,
                mc: captureData.mc,
                srno: captureData.srno,
              },
              biometricData: {
                pidData: captureData.pidData,
                sessionKey: captureData.sessionKey,
                hmac: captureData.hmac,
                ci: captureData.ci,
              },
              quality: {
                score: captureData.qScore,
                nmPoints: captureData.nmPoints,
              },
              xmlResponse: data,
              timestamp: new Date().toISOString(),
            },
          });
        },
        error: (jqXHR, textStatus, errorThrown) => {
          reject(new Error(`Capture failed: ${textStatus} - ${errorThrown}`));
        },
      });
    });
  }

  parseCaptureResponse(xmlString) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    const resp = xmlDoc.querySelector("Resp");
    const deviceInfo = xmlDoc.querySelector("DeviceInfo");
    const skey = xmlDoc.querySelector("Skey");
    const hmac = xmlDoc.querySelector("Hmac");
    const data = xmlDoc.querySelector("Data");

    const additionalInfo = deviceInfo?.querySelector("additional_info");
    const params = additionalInfo
      ? Array.from(additionalInfo.querySelectorAll("Param"))
      : [];
    let srno = "";
    const srnoParam = params.find((p) => p.getAttribute("name") === "srno");
    if (srnoParam) srno = srnoParam.getAttribute("value") || "";
    if (!srno && deviceInfo?.getAttribute("srno"))
      srno = deviceInfo.getAttribute("srno");

    return {
      errorCode: resp?.getAttribute("errCode") || "-1",
      errorInfo: resp?.getAttribute("errInfo") || "Unknown error",
      fCount: resp?.getAttribute("fCount") || "0",
      fType: resp?.getAttribute("fType") || "0",
      nmPoints: resp?.getAttribute("nmPoints") || "0",
      qScore: resp?.getAttribute("qScore") || "0",
      dpId: deviceInfo?.getAttribute("dpId") || "",
      rdsId: deviceInfo?.getAttribute("rdsId") || "",
      rdsVer: deviceInfo?.getAttribute("rdsVer") || "",
      dc: deviceInfo?.getAttribute("dc") || "",
      mi: deviceInfo?.getAttribute("mi") || "",
      mc: deviceInfo?.getAttribute("mc") || "",
      srno: srno,
      ci: skey?.getAttribute("ci") || "",
      sessionKey: skey?.textContent || "",
      hmac: hmac?.textContent || "",
      pidDataType: data?.getAttribute("type") || "",
      pidData: data?.textContent || "",
    };
  }

  /**
   * Build XML according to device type:
   * - Mantra: includes pgCount, pTimeout, and <CustOpts>
   * - IDEMIA/Morpho: includes iType, pType, posh="", no extra tags
   */
  buildPidOptionsXml(deviceInfo, opts) {
    const isMantra = deviceInfo.isMantra === true;
    console.log("isMantra");
    console.log(deviceInfo.isMantra);

    console.log("Building PID XML with wadh:", opts.wadh);

    const wadhAttr = opts.wadh ? ` wadh="${opts.wadh}"` : "";
    const otpAttr = opts.otp ? ` otp="${opts.otp}"` : "";

    if (isMantra) {
      return `<?xml version="1.0"?>
    <PidOptions ver="1.0">
      <Opts 
        fCount="1"
        fType="2"
        iCount="0"
        pCount="0"
        pgCount="2"
        format="0"
        pidVer="2.0"
        timeout="10000"
        pTimeout="20000"
        posh="UNKNOWN"
        env="P"
        wadh="${opts.wadh}"
      />
      <CustOpts>
        <Param name="mantrakey" value=""/>
      </CustOpts>
    </PidOptions>`;
    } else {
      return `<PidOptions ver="1.0"><Opts env="P" fCount="1" fType="2" format="0" pidVer="2.0" timeout="10000" otp="" wadh="${opts.wadh}" posh=""/></PidOptions>`;
    }

    //return `<?xml version="1.0"?> <PidOptions ver="1.0"> <Opts fCount="1" fType="2" iCount="0" pCount="0" pgCount="2" format="0"   pidVer="2.0" timeout="10000" pTimeout="20000" posh="UNKNOWN" env="P" wadh="18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=" /> <CustOpts><Param name="mantrakey" value="" /></CustOpts> </PidOptions>`;

    // // For IDEMIA, ensure iType and pType are present (empty if not provided)
    // const iTypeAttr =
    //   opts.iType !== undefined ? ` iType="${opts.iType}"` : ' iType=""';
    // const pTypeAttr =
    //   opts.pType !== undefined ? ` pType="${opts.pType}"` : ' pType=""';

    // // Common attributes (order may matter for strict parsers)
    // let optTag = `<Opts env="${opts.env}" fCount="${opts.fCount}" fType="${opts.fType}" iCount="${opts.iCount}"${iTypeAttr} pCount="${opts.pCount}"${pTypeAttr} format="${opts.format}" pidVer="${opts.pidVer}" timeout="${opts.timeout}" otp="${opts.otp}" wadh="${opts.wadh}" posh="${opts.posh}"`;

    // if (isMantra) {
    //   optTag += ` pgCount="2" pTimeout="20000"`;
    // }
    // optTag += ` />`;

    // let xml = `<?xml version="1.0"?> <PidOptions ver="1.0"> ${optTag} `;
    // if (isMantra) {
    //   xml += `<CustOpts><Param name="mantrakey" value="" /></CustOpts>`;
    // }
    // xml += `</PidOptions>`;
    // //  return xml;

    // //  return '<?xml version="1.0"?><PidOptions ver="1.0"><Opts fCount="1" fType="1" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="10000" posh="UNKNOWN" env="P" wadh="18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=" /><CustOpts></CustOpts></PidOptions>';

    // return `<PidOptions ver="1.0"><Opts env="P" fCount="1" fType="1" format="0" pidVer="2.0" timeout="10000" otp="" wadh="18f4CEiXeXcfGXvgWA/blxD+w2pw7hfQPY45JMytkPw=" posh=""/></PidOptions>`;
  }

  async captureBiometric(options = {}) {
    let lastError = null;
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        console.log(`Biometric capture attempt ${attempt}/${this.MAX_RETRIES}`);
        const status = await this.checkServiceStatus();
        if (!status.serviceAvailable) throw new Error(status.error);
        const captureResult = await this.captureFingerprint(
          status.deviceInfo,
          options,
        );
        if (captureResult.success) {
          return { success: true, attempt: attempt, ...captureResult };
        }
        lastError = new Error(captureResult.error);
      } catch (error) {
        lastError = error;
        if (attempt < this.MAX_RETRIES) await this.delay(1000 * attempt);
      }
    }
    return {
      success: false,
      error:
        lastError?.message || "Biometric capture failed after all attempts",
      errorCode: "ALL_ATTEMPTS_FAILED",
    };
  }

  convertXmlToBase64(xmlString) {
    try {
      return btoa(unescape(encodeURIComponent(xmlString.trim())));
    } catch (error) {
      console.error("Error converting XML to base64:", error);
      throw new Error("Failed to convert biometric data");
    }
  }

  getTroubleshootingGuide() {
    return {
      commonIssues: [
        {
          issue: "RD Service not running",
          solution:
            "Ensure RD Service is installed and running on your system (check system tray)",
        },
        {
          issue: "Device not detected",
          solution: "Check USB connection and restart RD Service",
        },
        {
          issue: "Chrome security error",
          solution:
            "Enable 'Allow invalid certificates for localhost' in chrome://flags",
        },
        {
          issue: "Permission denied",
          solution: "Run the application with administrator privileges",
        },
        {
          issue: "Fingerprint capture failed",
          solution: "Clean the fingerprint scanner and try again",
        },
        {
          issue: "CORS error",
          solution: "Ensure RD Service is running and accessible from browser",
        },
      ],
      stepsToVerify: [
        "1. Open RD Service Client webpage (http://127.0.0.1:11100) in browser",
        "2. Click 'Discover AVDM' button",
        "3. If device is found, click 'Capture' button",
        "4. If fingerprint is captured successfully, the device is working properly",
      ],
      downloadLinks: {
        mantratec: "https://www.mantratec.com/downloads",
        morpho: "https://www.idemia.com/support",
        starTek: "https://www.startek.com/support",
        precision: "https://www.precisionbiometric.com/support",
        secuGen: "https://www.secugen.com/downloads",
      },
      supportedDevices: [
        "Mantra MFS100",
        "Morpho MSO 1300 E3",
        "Startek FM220",
        "Precision P320",
        "SecuGen Hamster Pro",
        "All L1 compliant devices",
      ],
    };
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

const biometricService = new BiometricService();
export default biometricService;
